import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, Ban, Flag, MessageSquare, MoreHorizontal, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useMeta, eventJsonLd } from "@/lib/seo";
import { Avatar } from "@/components/AttendeesModal";

const loc = lang => lang === "de" ? "de-DE" : "en-GB";
const fmtTime = (s, lang) => new Date(s).toLocaleTimeString(loc(lang), { hour: "2-digit", minute: "2-digit" });
const dayKey = s => new Date(s).toDateString();
const dayLabel = (s, lang, t) => {
  const d = new Date(s), now = new Date(), y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return t("today");
  if (d.toDateString() === y.toDateString()) return t("yesterday");
  return d.toLocaleDateString(loc(lang), { day: "2-digit", month: "short", ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}) }).toUpperCase();
};

function Thread({ cid, onBack, onDeleted }) {
  const { t, lang, user } = useApp();
  const [c, setC] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const end = useRef();
  const del = async () => {
    try { await api.delete(`/messages/conversations/${cid}`); toast.success(t("conversationDeleted")); onDeleted(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const load = () => api.get(`/messages/conversations/${cid}`).then(r => setC(r.data)).catch(() => setC(false));
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [cid]); // eslint-disable-line
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [c?.messages?.length]);
  if (c === false) return <div className="p-6 eyebrow">{t("notFound")}</div>;
  if (!c) return <div className="p-6 eyebrow">…</div>;

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/messages/conversations/${cid}`, { text }); setC({ ...c, messages: [...c.messages, data] }); setText(""); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const toggleBlock = async () => {
    try { c.blocked ? await api.delete(`/users/${c.other.user_id}/block`) : await api.post(`/users/${c.other.user_id}/block`); toast.success(c.blocked ? t("unblocked") : t("blocked")); load(); }
    catch { toast.error("Failed"); }
  };
  const doReport = async () => {
    try { await api.post(`/users/${c.other.user_id}/report`, { reason, conversation_id: cid }); toast.success(t("reported")); setReport(false); setReason(""); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const locked = c.blocked || c.blocked_me || c.other.deleted;

  return (
    <div className="flex flex-col h-full" data-testid="message-thread">
      <div className="flex items-center gap-3 pb-3 border-b border-foreground">
        <button onClick={onBack} className="gg-icon-btn md:hidden" aria-label="back" data-testid="thread-back"><ArrowLeft className="w-5 h-5" /></button>
        {c.other.deleted
          ? <span className="flex items-center gap-2 min-w-0 text-muted-foreground" data-testid="thread-deleted-user"><Avatar user={c.other} /><span className="font-display uppercase text-lg truncate" data-testid="thread-name">{t("deletedUser")}</span></span>
          : <Link to={`/users/${c.other.user_id}`} className="flex items-center gap-2 min-w-0 hover:text-primary transition-colors"><Avatar user={c.other} /><span className="font-display uppercase text-lg truncate" data-testid="thread-name">{c.other.name}</span></Link>}
        <div className="ml-auto">
          {!c.other.deleted && <DropdownMenu>
            <DropdownMenuTrigger asChild><button className="gg-icon-btn" aria-label={t("more")} data-testid="thread-menu"><MoreHorizontal className="w-5 h-5" /></button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none border-foreground bg-background min-w-[200px]" data-testid="thread-menu-content">
              <DropdownMenuItem onClick={() => setConfirmDel(true)} className="rounded-none font-display uppercase text-xs tracking-wider gap-2" data-testid="menu-delete"><Trash2 className="w-4 h-4" />{t("deleteConversation")}</DropdownMenuItem>
              <DropdownMenuItem onClick={toggleBlock} className="rounded-none font-display uppercase text-xs tracking-wider gap-2" data-testid="menu-block"><Ban className="w-4 h-4" />{c.blocked ? t("unblockUser") : t("blockUser")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setReport(true)} className="rounded-none font-display uppercase text-xs tracking-wider gap-2" data-testid="menu-report"><Flag className="w-4 h-4" />{t("reportUser")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>}
        </div>
      </div>
      {c.event_name && <Link to={`/events/${c.event_slug}`} className="mt-2 text-sm hover:text-primary" data-testid="thread-regarding"><span className="eyebrow !text-[10px] text-primary mr-2">{t("regarding")}:</span>{c.event_name} →</Link>}
      {confirmDel && (
        <div className="mt-3 border border-foreground p-3 flex flex-wrap items-center justify-between gap-3" data-testid="delete-confirm">
          <span className="font-display uppercase text-sm">{t("deleteConversationQ")}</span>
          <div className="flex gap-2"><button onClick={() => setConfirmDel(false)} className="gg-btn !py-2 !px-3 text-xs" data-testid="delete-cancel">{t("cancel")}</button><button onClick={del} className="gg-btn-primary !py-2 !px-3 text-xs" data-testid="delete-confirm-btn">{t("delete")}</button></div>
        </div>
      )}
      {report && (
        <div className="mt-3 border border-foreground p-3" data-testid="report-box">
          <div className="eyebrow mb-2">{t("reportUser")}</div>
          <textarea rows={2} className="gg-input" placeholder={t("reportReason")} value={reason} onChange={e => setReason(e.target.value)} data-testid="report-reason" />
          <div className="mt-2 flex gap-2 justify-end"><button onClick={() => setReport(false)} className="gg-btn !py-2 !px-3 text-xs">{t("cancel")}</button><button onClick={doReport} disabled={!reason.trim()} className="gg-btn-primary !py-2 !px-3 text-xs" data-testid="report-send">{t("sendReport")}</button></div>
        </div>
      )}
      <div className="flex-1 overflow-y-auto py-4 space-y-3" data-testid="thread-messages">
        {c.messages.map((m, i) => {
          const mine = m.sender_id === user.user_id;
          const newDay = i === 0 || dayKey(m.created_at) !== dayKey(c.messages[i - 1].created_at);
          return (
            <React.Fragment key={m.message_id}>
              {newDay && <div className="flex items-center gap-3 py-1" data-testid="day-separator"><span className="flex-1 border-t border-foreground/20" /><span className="eyebrow !text-[10px] text-muted-foreground">{dayLabel(m.created_at, lang, t)}</span><span className="flex-1 border-t border-foreground/20" /></div>}
              <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 border border-foreground ${mine ? "bg-foreground text-background" : "bg-background"}`} data-testid={`msg-${m.message_id}`}>
                  <div className="whitespace-pre-wrap leading-relaxed text-sm">{m.text}</div>
                  <div className={`mt-1 text-[10px] tracking-wider ${mine ? "text-background/70" : "text-muted-foreground"}`}>{fmtTime(m.created_at, lang)}</div>
                </div>
              </div>
            </React.Fragment>);
        })}
        <div ref={end} />
      </div>
      {locked ? <p className="text-sm text-muted-foreground border-t border-foreground pt-3" data-testid="thread-locked">{c.blocked ? t("youBlocked") : t("cannotMessage")}</p> : (
        <div className="flex gap-2 border-t border-foreground pt-3">
          <textarea rows={2} maxLength={2000} className="gg-input flex-1 !py-2" placeholder={t("messagePlaceholder")} value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="thread-input" />
          <button onClick={send} disabled={busy || !text.trim()} className="gg-btn-primary !px-3" aria-label={t("send")} data-testid="thread-send"><Send className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

export default function Messages() {
  const { cid } = useParams();
  const { t, lang, user, checkingAuth } = useApp();
  useMeta({ title: t("messages"), description: "", path: "/messages", noindex: true });
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  useEffect(() => { if (user) api.get("/messages/conversations").then(r => setItems(r.data.items)).catch(() => setItems([])); }, [user, cid]);
  if (checkingAuth) return null;
  if (!user) return <Navigate to="/auth" />;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8" data-testid="messages-page">
      <div className="eyebrow text-primary">{t("account")}</div>
      <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tighter border-b border-foreground pb-3 flex items-center gap-3"><MessageSquare className="w-7 h-7 text-primary" />{t("messages")}</h1>
      <div className="mt-6 grid md:grid-cols-[300px_1fr] gap-6 md:min-h-[60vh]">
        <ul className={`divide-y divide-foreground border-y border-foreground ${cid ? "hidden md:block" : ""}`} data-testid="conversation-list">
          {items === null && <li className="p-4 eyebrow">…</li>}
          {items?.length === 0 && <li className="p-6 text-sm text-muted-foreground" data-testid="conversations-empty">{t("noMessagesYet")}</li>}
          {items?.map(c => (
            <li key={c.conversation_id}>
              <button onClick={() => nav(`/messages/${c.conversation_id}`)} className={`w-full text-left p-3 flex items-start gap-3 hover:bg-muted transition-colors ${c.conversation_id === cid ? "bg-muted" : ""}`} data-testid={`conversation-${c.conversation_id}`}>
                <Avatar user={c.other} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2"><span className={`font-display uppercase text-base truncate ${c.unread > 0 ? "font-semibold" : ""}`}>{c.other.deleted ? t("deletedUser") : c.other.name}</span>{c.unread > 0 && <span className="ml-auto shrink-0 w-2 h-2 bg-primary" aria-label="unread" data-testid="conversation-unread" />}</span>
                  {c.event_name && <span className="block text-[10px] uppercase tracking-wider text-primary truncate">{c.event_name}</span>}
                  <span className={`block text-xs truncate ${c.unread > 0 ? "text-foreground font-medium" : "text-muted-foreground"}`}>{c.last_message}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
        <div className={`${cid ? "" : "hidden md:flex"} md:border-l md:border-foreground md:pl-6 flex flex-col min-h-[50vh]`}>
          {cid ? <Thread key={cid} cid={cid} onBack={() => nav("/messages")} onDeleted={() => { setItems(items => (items || []).filter(x => x.conversation_id !== cid)); nav("/messages"); }} /> : <p className="text-sm text-muted-foreground m-auto">{t("pickConversation")}</p>}
        </div>
      </div>
    </div>
  );
}
